import clsx from "clsx"
import React, { useEffect, useState } from "react"

import { ModalConfirm } from "@/components/common/element/ModalConfirm"
import { SwiftPicker } from "@/components/common/element/fields/SwiftPicker"
import { CommonInput } from "@/components/common/element/fields/CommonInput"
import Button from "@/ui/components/Button"
import * as TrinityAPI from "@/requests/TrinityAPI"

import type { ICompanyBank } from "@/types/company/IFullCompanyDto"
import type { IModalMessage } from "@/types/error/IModalMessage"
import type { IErrorModel } from "@/types/error/IErrorModel"
import type { ISwift } from "@/types/dict/ISwift"

import bankStyle from "../style.module.scss"
import { CurrencyPicker } from "@/components/common/element/fields/CurrencyPicker"

export const BankTableItem: React.FC<{
  bank: ICompanyBank
  onRemove: (bankId: number) => void
  refetch: () => void
}> = ({ bank, onRemove, refetch }) => {
  const [modalConfirm, setModalConfirm] = useState<IModalMessage>()
  const [isEditOpen, setEditOpen] = useState(false)
  const [bankData, setBankData] = useState<ICompanyBank>(bank)
  const [errors, setErrors] = useState<IErrorModel>()
  const [isSaving, setSaving] = useState(false)

  const toggleEditOpen = () => {
    setEditOpen(!isEditOpen)
  }

  const changeSwift = (swift: ISwift | undefined) => {
    if (swift) {
      setBankData({
        ...bankData,
        swift: swift.swift_code,
        ...(swift.bank_name && {
          name: swift.bank_name
        })
      })
    }
  }

  const changeCurrency = (currency: string) => {
    setBankData({ ...bankData, currency: currency })
  }

  const onSave = () => {
    if (isSaving || !bankData) {
      return
    }

    setSaving(true)

    const payload = {
      id: Number(bankData.id),
      currencyPayerId: Number(bankData.currencyPayerId),
      name: bankData.name,
      swift: bankData.swift,
      accountNumber: bankData.accountNumber,
      currency: bankData.currency
    }

    TrinityAPI.updateCurrencyPayerBank(
      payload,
      response => {
        if (response.status === 200) {
          setEditOpen(false)
          refetch()
        } else {
          setErrors(response.data)
        }
      },
      () => setSaving(false)
    )
  }

  const confirmRemove = (val: any) => {
    if (val) onRemove(Number(bank.id))
  }

  useEffect(() => {
    setBankData({
      ...bank
    })
  }, [isEditOpen])

  return (
    <>
      <ModalConfirm message={modalConfirm} setMessage={setModalConfirm} />
      <li className="global-item">
        <p className={`global-item-text global-name cols-4`}>
          {isEditOpen ? (
            <CommonInput
              typeName={"text"}
              placeholder={"PT GAS"}
              name={"name"}
              value={bankData?.name}
              errorModel={errors}
              _callback={e => setBankData({ ...bankData, name: e })}
            />
          ) : (
            bank.name
          )}
        </p>
        <p className="global-item-text global-bank cols-4">
          {isEditOpen ? (
            <SwiftPicker name={"swift"} value={bankData?.swift} errorModel={errors} _callback={changeSwift} />
          ) : (
            bank.swift
          )}
        </p>
        <p className="global-item-text globak-swift cols-4">
          {isEditOpen ? (
            <CommonInput
              typeName={"text"}
              placeholder={"23823044"}
              name={"accountNumber"}
              value={bankData?.accountNumber}
              errorModel={errors}
              _callback={e => setBankData({ ...bankData, accountNumber: e })}
            />
          ) : (
            bank.accountNumber
          )}
        </p>
        <p className="global-item-text globak-num cols-4">
          {isEditOpen ? (
            <CurrencyPicker
              name={"currency"}
              errorModel={errors}
              initValue={bank?.currency}
              _callback={changeCurrency}
            />
          ) : (
            bank.currency
          )}
        </p>
        <div className={clsx("global-item-text globak-num cols-4", bankStyle.itemButtons)}>
          {isEditOpen ? (
            <>
              <Button onClick={onSave} loading={isSaving}>
                Сохранить
              </Button>
              <Button onClick={toggleEditOpen} variant="black">
                Отменить
              </Button>
            </>
          ) : (
            <>
              <Button onClick={toggleEditOpen}>Редактировать</Button>
              <Button
                onClick={() =>
                  setModalConfirm({
                    title: "Удалить банк?",
                    text: `Вы действительно желаете удалить банк ${bank?.name}?`,
                    callbackValue: true,
                    _callback: confirmRemove
                  })
                }
                variant="black"
              >
                Удалить
              </Button>
            </>
          )}
        </div>
      </li>
    </>
  )
}
